import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Dropdown } from 'react-bootstrap';
import './SubmitOrderModal.css';
import TauriLoader2 from '../../TauriLoader2/TauriLoader2';
import SignatureDrawing from '../../OpalCoverageLayout/CoverageView/FinishSessionModal/SignatureDrawing/SignatureDrawing';
import { getAll, getRecordById } from '../../../localDB';
import OpalDataTable from '../../OpalDataTable/OpalDataTable';
import { formatNumber } from '../../../utils/NumberFormatter';

const SubmitOrderModal = ({ show, onHide, isSubmitting, onSubmitting, id }) => {
    const [signatureImage, setSignatureImage] = useState(null);
    const [loadingData, setLoadingData] = useState(false);
    const [order, setOrder] = useState(null);
    const [orderItems, setOrderItems] = useState([]);
    const [signatureError, setSignatureError] = useState('');


    const fetchOrder = async (OrderID = id) => {
        setLoadingData(true);
        try {
            const _order = await getRecordById("Orders", OrderID);
            const localOrderDetails = await getAll("OrderDetails");
            const _orderItems = localOrderDetails.filter(a => a.OrderID === OrderID);

            setOrder(_order);
            setOrderItems(_orderItems);
        } catch {

        } finally {
            setLoadingData(false);
        }
    }

    useEffect(() => {

        if (show) {
            setSignatureImage(null);
            setSignatureError('');
        }

        if (id) {
            fetchOrder(id);
        }
    }, [show, id])

    const orderDetailColumns = [
        { key: "Item.Description", label: "ITEM" },
        { key: "Unit.Description", label: "UNIT" },
        {
            key: "DetailPrice",
            label: "PRICE",
            inputType: "number"
        },
        {
            key: "Quantity",
            label: "QUANTITY",
            inputType: "number"
        },
        {
            key: "FreeGoods",
            label: "FREE GOODS",
            inputType: "number"
        },
        {
            key: "DiscountPercent",
            label: "DISCOUNT %",
            inputType: "number"
        },
        {
            key: "NetLineTotal",
            label: "NET AMOUNT",
            inputType: "number",
            total: true
        }
    ];

    function getExtensionFromBlob(blob) {
        if (!blob || !blob.type) return '';
        const mime = blob.type; // e.g., "image/png"
        const ext = mime.split('/')[1]; // get "png"
        return ext || '';
    }

    function formatDateForFilename(date = new Date()) {
        const pad = (num) => String(num).padStart(2, '0');
        return (
            date.getFullYear().toString() +
            pad(date.getMonth() + 1) +
            pad(date.getDate()) +
            pad(date.getHours()) +
            pad(date.getMinutes()) +
            pad(date.getSeconds())
        );
    }

    const handleSubmit = () => {
        if (!signatureImage) {
            setSignatureError('Signature is required');
            return;
        }

        const ext_id = localStorage.getItem("ext_id");
        const formattedDate = formatDateForFilename(new Date());
        const signatureExt = getExtensionFromBlob(signatureImage) || 'png';
        const _filename = `order_${ext_id}_${formattedDate}_${order?.Account?.ID}.${signatureExt}`

        const _file = {
            FileName: _filename,
            ImageData: signatureImage
        }

        setSignatureError('');
        onSubmitting(_file);
    }

    return (
        <Modal
            className='submit-order-modal'
            show={show}
            onHide={onHide}
            size="lg"
            centered
            backdrop={isSubmitting ? "static" : true}
            keyboard={!isSubmitting}
        >
            <Modal.Header>Submit Order</Modal.Header>

            <Modal.Body>
                {loadingData ? (
                    <div className="d-flex justify-content-center align-items-center flex-grow-1">
                        <TauriLoader2 width="2.5rem" height="2.5rem" borderwidth="0.35rem" color="#1D5D9B" />
                    </div>
                ) : (
                    <>
                        {/* Sales Summary */}
                        <div className="sales-summary">
                            <div className="summary-header">ORDER SUMMARY</div>

                            {/* Order Info */}
                            <table className="summary-table">
                                <colgroup>
                                    <col style={{ width: '75%' }} />
                                    <col style={{ width: '25%' }} />
                                </colgroup>
                                <tbody>
                                    <tr>
                                        <td>
                                            <p><span className='summary-label me-1'>ACCOUNT:</span> {order?.Account?.Name ?? '-'}</p>
                                            <p className="inline-wrap">
                                                <span className='summary-label me-1'>DELIVERY ADDRESS:</span>
                                                <span className='summary-value'>
                                                    {order?.DeliveryAddress ?? '-'}
                                                </span>
                                            </p>
                                        </td>
                                        <td>
                                            <p><span className='summary-label me-1'>ORDER DATE:</span> {order?.OrderDate ?? '-'}</p>
                                            <p><span className='summary-label me-1'>ORDER NO.:</span> {order?.OrderNumber ?? '-'}</p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2}>
                                            <p>
                                                <span className='summary-label me-1'>REMARKS:</span>
                                                <span className='summary-value'>
                                                    {order?.Remarks ?? '-'}
                                                </span>
                                            </p>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <OpalDataTable
                                data={orderItems}
                                columns={orderDetailColumns}
                            />

                            <div className='summary-totals d-flex align-items-center justify-content-end gap-4 mt-2'>
                                <div className='d-flex gap-1'>
                                    <span className="summary-label">GROSS AMOUNT:</span>
                                    <span className='summary-value'>{formatNumber(order?.GrossTotal)}</span>
                                </div>
                                <div className='d-flex gap-1'>
                                    <span className="summary-label">DISCOUNT AMOUNT:</span>
                                    <span className='summary-value'>{formatNumber(order?.TotalDiscount)}</span>
                                </div>
                                <div className='d-flex gap-1'>
                                    <span className="summary-label">NET AMOUNT:</span>
                                    <span className='summary-value'>{formatNumber(order?.NetTotal)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="sales-order-summary-signature flex-grow-1">
                            <SignatureDrawing
                                onSignatureChange={setSignatureImage}
                                Signature={signatureImage}
                            />
                        </div>
                        <div className="submit-actions mt-3 d-flex justify-content-end align-items-center gap-3">
                            {signatureError && (
                                <span className="text-danger me-auto">{signatureError}</span>
                            )}

                            <Button
                                className='btn-submit d-flex align-items-center'
                                disabled={isSubmitting}
                                onClick={handleSubmit}
                            >
                                {isSubmitting && (
                                    <TauriLoader2
                                        width="1.2rem"
                                        height="1.2rem"
                                        borderwidth="0.2rem"
                                        color="#1D5D9B"
                                    />
                                )}
                                <span className="text-nowrap">Submit</span>
                            </Button>

                            <Button
                                className="btn-cancel d-flex"
                                disabled={isSubmitting}
                                onClick={onHide}
                            >
                                <span className="text-nowrap">Close</span>
                            </Button>
                        </div>

                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default SubmitOrderModal;
